import { UIEventHandler, useCallback, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { observer } from 'mobx-react';
import { useMessagingStore } from '../hooks/use-messaging-store';
import { MessageRow } from './message-row';
import { Flex, Box, Center, Text } from '@cad-arc';
import { LoadingIndicator } from '../../common/components/loading-indicator';
import { useMessageSenderColor } from '../hooks/use-message-sender-color';
import { useMarkVisibleAsRead } from '../hooks/use-mark-visible-as-read';
import { UnreadMessagesButton } from './unread-messages-button';
import { useScrollToMessage } from '../hooks/use-scroll-to-message';
import { MessageLoadFailBanner } from './message-load-fail-banner';
import { colors } from '../../colors/colors';

const SINGLE_ROW_HEIGHT_PX = 76;
const MESSAGE_LOAD_FAIL_BANNER_TEST_ID = 'conversation-messages-load-retry';

const UnreadLine = ({ isUrgent }: { isUrgent: boolean }) => {
    const color = isUrgent ? 'var(--arc-colors-urgent-default)' : colors.messagesUnreadLineNormal;
    return (
        <Center width="100%" height="var(--arc-space-4)">
            <Flex flexDirection="row" width="100%">
                <Center flexGrow={1}>
                    <Flex flexGrow={1} borderTop={`1px solid ${color}`}></Flex>
                </Center>
                <Box padding="0 var(--arc-space-1)">
                    <Text variant="headingXs" style={{ color }}>
                        {isUrgent ? 'Urgent' : 'New'}
                    </Text>
                </Box>
            </Flex>
        </Center>
    );
};

export const MessageRenderer = observer(({ ...props }) => {
    const {
        conversationMetadata,
        selectedConversationKey,
        selectedConversationMessages,
        selectedConversation,
        trackConversationScroll,
        getMessageIndexById,
        conversationMessagesFetchState,
    } = useMessagingStore();

    const unreadInfo = useMemo(() => {
        return selectedConversation?.assignedParticipantUnreadInfos.find(
            (unreadInfo) =>
                unreadInfo.participantId === selectedConversationKey?.participantIdentity?.id
        );
    }, [
        selectedConversation?.assignedParticipantUnreadInfos,
        selectedConversationKey?.participantIdentity?.id,
    ]);

    const getItemKey = useCallback(
        (index) => {
            return selectedConversationMessages === undefined ||
                selectedConversationMessages[index]?.id === undefined
                ? 0
                : selectedConversationMessages[index].id;
        },
        [selectedConversationMessages]
    );

    const scrollElementRef = useRef<HTMLDivElement | null>(null);

    const estimateRowSize = (): number => SINGLE_ROW_HEIGHT_PX;

    const messageRowVirtualizer = useVirtualizer({
        count: selectedConversationMessages === undefined ? 0 : selectedConversationMessages.length,
        getItemKey,
        getScrollElement: () => scrollElementRef.current,
        estimateSize: estimateRowSize,
        overscan: 0,
    });

    const virtualMessageRows = messageRowVirtualizer.getVirtualItems();
    const virtualMessageRowsStart = virtualMessageRows?.[0]?.start || 0;
    const totalSize = messageRowVirtualizer.getTotalSize();

    const getColor = useMessageSenderColor(selectedConversationKey);

    useScrollToMessage(messageRowVirtualizer, selectedConversationMessages, conversationMetadata);

    const newestMessageId = selectedConversation?.newestMessageId;
    const oldestUnreadMessageId = unreadInfo?.oldestUnreadMessageId;
    useMarkVisibleAsRead(
        messageRowVirtualizer,
        virtualMessageRows,
        oldestUnreadMessageId,
        newestMessageId
    );

    const scrollEventHandler: UIEventHandler<HTMLDivElement> = useCallback(
        (event) => {
            const scrollElement = event.target as HTMLElement;
            if (scrollElement !== null && selectedConversationKey !== undefined) {
                trackConversationScroll(selectedConversationKey, scrollElement);
            }
        },
        [selectedConversationKey, trackConversationScroll]
    );

    if (selectedConversationMessages === undefined) {
        return (
            <Box h="100%">
                <MessageLoadFailBanner
                    isError={conversationMessagesFetchState?.isError}
                    refetch={conversationMessagesFetchState?.refetch}
                    testId={MESSAGE_LOAD_FAIL_BANNER_TEST_ID}
                />
                {conversationMessagesFetchState?.isFetching && <LoadingIndicator />}
            </Box>
        );
    }

    return (
        <>
            <MessageLoadFailBanner
                isError={conversationMessagesFetchState?.isError}
                refetch={conversationMessagesFetchState?.refetch}
                testId={MESSAGE_LOAD_FAIL_BANNER_TEST_ID}
            />
            <Box position="relative" overflow="auto" height="100%" width="100%" margin={0}>
                <Box
                    data-testid="messages-scroll-element"
                    position="absolute"
                    overflow="auto"
                    height="100%"
                    width="100%"
                    flex="1"
                    ref={scrollElementRef}
                    onScroll={scrollEventHandler}
                >
                    <Box width="100%" position="relative" margin="0" height={totalSize}>
                        <Flex
                            flexDirection="column"
                            position="absolute"
                            top={0}
                            left={0}
                            width="100%"
                            transform={`translateY(${virtualMessageRowsStart}px)`}
                        >
                            {virtualMessageRows.map((row) => {
                                const message = selectedConversationMessages[row.index];
                                const isFirstMessage = row.index === 0;
                                const isLastMessage =
                                    row.index === selectedConversationMessages.length - 1;
                                const isOldestUnreadMessage =
                                    unreadInfo?.oldestUnreadMessageId === message.id;
                                const isUrgent = !!unreadInfo?.hasUrgentUnread;
                                return (
                                    <Flex
                                        flexDirection="column"
                                        key={row.key}
                                        data-testid="message-row"
                                        data-testisurgent={message.isUrgent}
                                        data-testmessageid={message.id}
                                        data-index={row.index}
                                        ref={messageRowVirtualizer.measureElement}
                                        width="100%"
                                    >
                                        {isOldestUnreadMessage && (
                                            <UnreadLine isUrgent={isUrgent} />
                                        )}
                                        <Flex
                                            alignSelf={message.isSender ? 'end' : 'start'}
                                            margin="0 !important"
                                            maxWidth="95%"
                                        >
                                            <Box
                                                // This Box is used to expand the margins of the message row. This needs to done on inner element
                                                // rather than on the outer Box of the message row in order for the scrolling to work correctly
                                                // (so that the virtualizer scrollToIndex() includes the margin correctly).
                                                marginTop={
                                                    isFirstMessage
                                                        ? 'var(--arc-space-3)'
                                                        : isOldestUnreadMessage
                                                        ? ''
                                                        : 'var(--arc-space-2)'
                                                }
                                                marginBottom={
                                                    isLastMessage ? 'var(--arc-space-3)' : '0'
                                                }
                                                marginLeft="var(--arc-space-3)"
                                                marginRight="var(--arc-space-3)"
                                            >
                                                <MessageRow
                                                    getSenderColor={getColor}
                                                    message={
                                                        selectedConversationMessages[row.index]
                                                    }
                                                    {...props}
                                                />
                                            </Box>
                                        </Flex>
                                    </Flex>
                                );
                            })}
                        </Flex>
                    </Box>
                </Box>
                <UnreadMessagesButton
                    messageRowVirtualizer={messageRowVirtualizer}
                    conversationMessages={selectedConversationMessages}
                    unreadInfo={unreadInfo}
                    getMessageIndexById={getMessageIndexById}
                    conversationKey={selectedConversationKey}
                />
            </Box>
        </>
    );
});

export default MessageRenderer;
